// Tiny IndexedDB-backed offline queue for lead creations & follow-ups.
// Replays automatically when the browser regains connectivity.

const DB_NAME = "sohom-offline";
const STORE = "queue";

function openDB() {
  return new Promise((resolve, reject) => {
    // First open without version to discover the existing one
    const probe = indexedDB.open(DB_NAME);
    probe.onerror = () => reject(probe.error);
    probe.onupgradeneeded = () => {
      // brand-new DB
      probe.result.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
    };
    probe.onsuccess = () => {
      const db = probe.result;
      if (db.objectStoreNames.contains(STORE)) {
        resolve(db);
        return;
      }
      // DB exists but the store doesn't — bump version and create store
      const nextVer = db.version + 1;
      db.close();
      const upReq = indexedDB.open(DB_NAME, nextVer);
      upReq.onupgradeneeded = () => {
        const db2 = upReq.result;
        if (!db2.objectStoreNames.contains(STORE)) {
          db2.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
        }
      };
      upReq.onsuccess = () => resolve(upReq.result);
      upReq.onerror = () => reject(upReq.error);
    };
  });
}

export async function enqueue(item) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).add({ ...item, createdAt: Date.now() });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export async function listQueue() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

export async function remove(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function flushQueue(apiBaseUrl, token) {
  let items;
  try {
    items = await listQueue();
  } catch {
    return { okCount: 0, failCount: 0, remaining: 0 };
  }
  let okCount = 0;
  let failCount = 0;
  for (const item of items) {
    try {
      const res = await fetch(`${apiBaseUrl}${item.url}`, {
        method: item.method || "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: item.body ? JSON.stringify(item.body) : undefined,
      });
      if (res.ok) {
        await remove(item.id);
        okCount++;
      } else {
        failCount++;
      }
    } catch {
      failCount++;
    }
  }
  return { okCount, failCount, remaining: items.length - okCount };
}

export async function queueSize() {
  try {
    const items = await listQueue();
    return items.length;
  } catch {
    return 0;
  }
}

// Expose for debugging in browser console / e2e tests
if (typeof window !== "undefined") {
  window.__sohomQueue = { enqueue, listQueue, remove, flushQueue, queueSize };
}
